import { CreditCardOutlined, DownOutlined, LeftOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar, Button, Collapse, Form, Input, notification } from "antd";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import InspectorInfoField from "@Components/groups/InspectorInfoField";
import InspectorModuleTitle from "@Components/groups/InspectorModuleTitle";
import { GroupContext, QueryKeys } from "@Constants";
import { MainContext } from "@Context/provider";
import { ICreditDetails, ICreditDetailsPaged } from "@Models/ICreditDetails";
import CreditService from "@Services/CreditService";
import { ICheckCapFull } from "./types";
import { useCreditDeleteGroup, useCreditDeleteUser } from "@Features/groups/state/mutations";
import { hasCapability } from "@Utils";

interface IProps {
    context: number;
    instance: string;
    checkCap: ICheckCapFull;
}

const ModuleCredit: React.FC<IProps> = ({ context, instance, checkCap }: IProps) => {
    const { ui: uiState } = useContext(MainContext);
    const { Panel } = Collapse;
    const [data, setData] = useState<ICreditDetails>(null);
    const [fetching, setFetching] = useState<boolean>(false);
    const [updating, setUpdating] = useState<boolean>(false);
    const stateRef = useRef<ICreditDetails>();
    const queryClient = useQueryClient();
    stateRef.current = data;
    const creditDeleteUserMutation = useCreditDeleteUser(instance);
    const creditDeleteGroupMutation = useCreditDeleteGroup();
    const allowDelete = hasCapability("Credit/Delete", context, instance);

    /**
     * Update the model with edited data.
     */
    const updateField = async () => {
        setUpdating(true);
        const service = new CreditService();
        await service.update(stateRef.current);

        notification.success({ message: "Credit account updated." });
        setUpdating(false);

        // Refresh the query for the user's credits, in case the above update affects it.
        await queryClient.invalidateQueries([QueryKeys.UserCredits]);
    };

    /**
     * Trigger an update two seconds after last keypress.
     */
    const debouncePromise = useCallback(AwesomeDebouncePromise(updateField, 2000), []);

    /**
     * Trigger an update to the form data state
     * @param field
     * @param value
     */
    const onInputChange = async (field, value) => {
        const formData = { ...data };
        formData[field] = value;
        setData(formData);
        await debouncePromise();
    };

    /**
     * Call the check capability methods passed from props with the appropriate context and instance
     * @param cap
     */
    const allowed = (cap: string) => {
        return checkCap(cap, context, instance);
    };

    /**
     * Fetch current user / group credit data.
     */
    useEffect(() => {
        stateRef.current = null;
        setData(null);

        const fetch = async () => {
            if (instance === null || instance === "") return;
            setFetching(true);
            const service = new CreditService();
            const credits =
                context === GroupContext ? await service.getGroup([instance]) : await service.getUser([instance]);
            if (!credits.hasErrors) {
                stateRef.current = Array.isArray(credits.value)
                    ? credits.value.find((x) => x.instanceId === instance)
                    : undefined;
                if (stateRef.current !== null) {
                    setData(stateRef.current);
                }
            }
        };

        const queryKey = context === GroupContext ? QueryKeys.UserGroupCredits : QueryKeys.GroupUserCredits;
        const query = queryClient.getQueryData<ICreditDetailsPaged[]>([queryKey]);
        const credit = query?.find((x) => x.contextId === context && x.instanceId === instance);
        if (credit !== undefined && credit !== null) {
            stateRef.current = credit;
            if (stateRef.current !== null) {
                setData(stateRef.current);
            }
        } else {
            fetch().then(() => {
                setFetching(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiState.currentGroup, uiState.currentUser]);

    return (
        data !== null && (
            <>
                <Collapse
                    destroyInactivePanel={false}
                    bordered={false}
                    ghost
                    expandIconPosition={"end"}
                    expandIcon={({ isActive }) => (
                        <Avatar
                            size="small"
                            className={"collapse-icon"}
                            icon={isActive ? <DownOutlined /> : <LeftOutlined />}
                        />
                    )}
                >
                    <Panel
                        header={
                            <InspectorModuleTitle
                                title={"Credit"}
                                slug={"credit"}
                                fetching={fetching || updating}
                                icon={<CreditCardOutlined />}
                            />
                        }
                        key={1}
                    >
                        <Form className="inspector-form" initialValues={{}}>
                            <Form.Item label="Credits">
                                {allowed("Credit/Update") ? (
                                    <Input
                                        name={"creditValue"}
                                        value={data?.creditValue}
                                        onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                    />
                                ) : (
                                    <InspectorInfoField>{data?.creditValue}</InspectorInfoField>
                                )}
                            </Form.Item>
                            <Form.Item label="Drawdowns">
                                {allowed("Credit/Update") ? (
                                    <Input
                                        name={"drawDownValue"}
                                        value={data?.drawDownValue}
                                        onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                    />
                                ) : (
                                    <InspectorInfoField>{data?.drawDownValue}</InspectorInfoField>
                                )}
                            </Form.Item>
                        </Form>
                        {/* something about addresses with allowdelete */}
                        {allowDelete ? (
                            <Button
                                className="secondary danger"
                                onClick={() => {
                                    uiState.currentUser?.instance
                                        ? creditDeleteUserMutation.mutateAsync(data)
                                        : creditDeleteGroupMutation.mutateAsync(data);
                                }}
                            >
                                Delete Credits
                            </Button>
                        ) : null}
                    </Panel>
                </Collapse>

                <hr />
            </>
        )
    );
};
export default ModuleCredit;
